/**
 * The progress of the files a form uploads when the user presses SAVE — the
 * companion of `useUploads`, which is for files that upload when PICKED.
 *
 * Those files are already on screen in their final place, marked pending; the
 * Save button spins while the save runs. This hook adds what the button
 * cannot say (owner's decision, 2026-10-18): which file is uploading and how
 * far it is. Wrap each upload of the save in `run(key, …)` with the key the
 * pending row or thumbnail renders by, and pass `progress[key]` as its
 * `upload` prop: the ring shows while that file goes, disappears when it
 * lands (the row is just pending again until the save finishes), and turns
 * red if it failed — the save stops and the user presses Save again.
 *
 * Retries on a network drop come from the engine the upload calls
 * (`lib/upload/retry.ts`). The screen is kept on while anything uploads.
 *
 * A failed ring shows, on hover, the text `errorMessage` makes of the error —
 * the app's translated reason (the tenant passes `uploadFailureText(t)`). By
 * default it is empty and the ring says the generic «failed» label: the
 * engine's raw message («upload refused: 400», «post status 502») never
 * reaches the user.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createWakeLock, type WakeLockController, type WakeLockEnv } from '../lib/wakeLock';
import type { UploadProgress, UploadState } from '../lib/upload/retry';

export interface UploadProgressTracker {
    /** The live progress of each file being uploaded, by its key. */
    progress: Readonly<Record<string, UploadProgress>>;
    /** Runs one upload under `key`. Resolves with its result; on failure the
     *  key stays as `failed` and the error is rethrown (the save stops). */
    run: <T>(key: string, upload: (options: { onState: (state: UploadState) => void }) => Promise<T>) => Promise<T>;
    /** Forgets every entry (a new save, or a discarded form). */
    clear: () => void;
}

export interface UploadProgressOptions {
    /** The text a failed ring shows for `error`. Default: none (the generic
     *  «failed» label). */
    errorMessage?: (error: unknown) => string;
}

export function useUploadProgress(options: UploadProgressOptions = {}): UploadProgressTracker {
    const [progress, setProgress] = useState<Record<string, UploadProgress>>({});
    const mounted = useRef(true);
    const errorMessageRef = useRef(options.errorMessage);
    errorMessageRef.current = options.errorMessage;

    const [wakeLock, setWakeLock] = useState<WakeLockController | null>(null);
    useEffect(() => {
        mounted.current = true;
        const lock = createWakeLock({
            wakeLock: typeof navigator === 'undefined'
                ? null
                : (navigator as unknown as { wakeLock?: WakeLockEnv['wakeLock'] }).wakeLock ?? null,
            document: typeof document === 'undefined' ? null : document,
        });
        setWakeLock(lock);
        return () => {
            mounted.current = false;
            lock.dispose();
        };
    }, []);

    const busy = Object.values(progress).some((p) => p.kind !== 'failed');
    useEffect(() => {
        if (!busy || !wakeLock) return undefined;
        return wakeLock.hold();
    }, [busy, wakeLock]);

    const set = useCallback((key: string, value: UploadProgress | null) => {
        if (!mounted.current) return;
        setProgress((prev) => {
            const next = { ...prev };
            if (value === null) delete next[key];
            else next[key] = value;
            return next;
        });
    }, []);

    const run = useCallback(async <T,>(
        key: string,
        upload: (options: { onState: (state: UploadState) => void }) => Promise<T>,
    ): Promise<T> => {
        set(key, { kind: 'uploading', loaded: 0, total: 0 });
        try {
            const result = await upload({ onState: (state) => set(key, state) });
            set(key, null);
            return result;
        } catch (error) {
            set(key, { kind: 'failed', message: errorMessageRef.current?.(error) ?? '' });
            throw error;
        }
    }, [set]);

    const clear = useCallback(() => {
        if (mounted.current) setProgress({});
    }, []);

    return { progress, run, clear };
}
